import React, { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  RefreshCw,
  Users,
  Clock,
  AlertTriangle,
  Lock,
  Sparkles,
  ChevronRight,
  Check,
  X,
  Edit2,
  Trash2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { teeSheetRulesAPI } from '../../services/api';
import './RulesPanel.css';

// Rule type display config
const RULE_TYPE_CONFIG = {
  partner_group: { icon: Users, label: 'Partner Groups', color: 'blue' },
  avoid_partner: { icon: X, label: 'Avoid Partner', color: 'red' },
  time_constraint: { icon: Clock, label: 'Time Constraints', color: 'orange' },
  time_preference: { icon: Clock, label: 'Time Preferences', color: 'yellow' },
  handicap_sort: { icon: Sparkles, label: 'Handicap Sort', color: 'purple' },
  pace_balance: { icon: Sparkles, label: 'Pace Balance', color: 'green' },
  manual_override: { icon: Lock, label: 'Manual Override', color: 'gray' },
  group_lock: { icon: Lock, label: 'Group Lock', color: 'indigo' },
  slot_lock: { icon: Lock, label: 'Slot Lock', color: 'indigo' }
};

// Status display config
const STATUS_CONFIG = {
  pending: { variant: 'secondary', label: 'Pending' },
  applied: { variant: 'success', label: 'Applied' },
  partial: { variant: 'warning', label: 'Partial' },
  failed: { variant: 'destructive', label: 'Failed' },
  overridden: { variant: 'outline', label: 'Overridden' },
  conflict: { variant: 'destructive', label: 'Conflict' }
};

function RulesPanel({
  tournamentId,
  onRuleApply,
  onRuleDelete,
  onConflictSelect,
  isCollapsed = false
}) {
  const [rules, setRules] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [stats, setStats] = useState({ total: 0, pending: 0, applied: 0, conflicts: 0, failed: 0 });
  const [conflicts, setConflicts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState(null);
  const [expandedTypes, setExpandedTypes] = useState(['partner_group', 'time_constraint']);

  // Fetch rules from API
  const fetchRules = useCallback(async () => {
    if (!tournamentId) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await teeSheetRulesAPI.getRules(tournamentId);
      if (response.data.success) {
        setRules(response.data.rules);
        setGrouped(response.data.grouped);
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching rules:', err);
      setError('Failed to load rules');
    } finally {
      setIsLoading(false);
    }
  }, [tournamentId]);

  // Fetch conflicts
  const fetchConflicts = useCallback(async () => {
    if (!tournamentId) return;

    try {
      const response = await teeSheetRulesAPI.getConflicts(tournamentId);
      if (response.data.success) {
        setConflicts(response.data.conflicts);
      }
    } catch (err) {
      console.error('Error fetching conflicts:', err);
    }
  }, [tournamentId]);

  // Extract rules from player data
  const handleExtractRules = async () => {
    if (!tournamentId) return;

    setIsExtracting(true);
    setError(null);
    try {
      const response = await teeSheetRulesAPI.extractRules(tournamentId, {
        clearExisting: true,
        includeSystemDefaults: true
      });

      if (response.data.success) {
        // Refresh rules after extraction
        await fetchRules();
        await fetchConflicts();
      }
    } catch (err) {
      console.error('Error extracting rules:', err);
      setError('Failed to extract rules');
    } finally {
      setIsExtracting(false);
    }
  };

  // Delete a rule
  const handleDeleteRule = async (ruleId) => {
    try {
      await teeSheetRulesAPI.deleteRule(ruleId);
      await fetchRules();
      onRuleDelete?.(ruleId);
    } catch (err) {
      console.error('Error deleting rule:', err);
    }
  };

  // Initial load
  useEffect(() => {
    fetchRules();
    fetchConflicts();
  }, [fetchRules, fetchConflicts]);

  // Render individual rule card
  const renderRuleCard = (rule) => {
    const typeConfig = RULE_TYPE_CONFIG[rule.type] || { icon: Sparkles, label: rule.type, color: 'gray' };
    const statusConfig = STATUS_CONFIG[rule.status] || { variant: 'secondary', label: rule.status };
    const IconComponent = typeConfig.icon;

    return (
      <div key={rule._id} className={cn("rule-card", `rule-${typeConfig.color}`, `status-${rule.status}`)}>
        <div className="rule-card-header">
          <div className="rule-icon">
            <IconComponent className="h-4 w-4" />
          </div>
          <div className="rule-info">
            <span className="rule-players">{rule.playerNames?.join(', ') || 'Unknown'}</span>
            {rule.parameters?.timeType && (
              <span className="rule-detail">
                {rule.parameters.timeType} {rule.parameters.targetTime || ''}
              </span>
            )}
          </div>
          <div className="rule-meta">
            <Badge variant={statusConfig.variant} className="status-badge">
              {statusConfig.label}
            </Badge>
            {rule.source === 'director_override' && (
              <Badge variant="outline" className="source-badge">
                <Lock className="h-3 w-3 mr-1" />
                Override
              </Badge>
            )}
          </div>
        </div>

        {rule.originalRequest && (
          <div className="rule-original-request">
            <span className="text-xs text-muted-foreground">"{rule.originalRequest}"</span>
          </div>
        )}

        {rule.conflicts && rule.conflicts.length > 0 && (
          <div className="rule-conflicts">
            <AlertTriangle className="h-3 w-3 text-destructive" />
            <span className="text-xs text-destructive">
              {rule.conflicts.filter(c => !c.resolved).length} unresolved conflict(s)
            </span>
          </div>
        )}

        <div className="rule-actions">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteRule(rule._id)}
            title="Delete rule"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  };

  // Render rule type section
  const renderRuleTypeSection = (type, typeRules) => {
    if (!typeRules || typeRules.length === 0) return null;

    const typeConfig = RULE_TYPE_CONFIG[type] || { icon: Sparkles, label: type, color: 'gray' };
    const IconComponent = typeConfig.icon;

    return (
      <AccordionItem key={type} value={type} className={cn("rule-type-section", `type-${typeConfig.color}`)}>
        <AccordionTrigger className="rule-type-trigger">
          <div className="rule-type-header">
            <IconComponent className="h-4 w-4" />
            <span>{typeConfig.label}</span>
            <Badge variant="secondary" className="rule-count">
              {typeRules.length}
            </Badge>
          </div>
        </AccordionTrigger>
        <AccordionContent className="rule-type-content">
          <div className="rules-list">
            {typeRules.map(rule => renderRuleCard(rule))}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  if (isCollapsed) {
    return (
      <div className="rules-panel collapsed">
        <div className="collapsed-indicator">
          <Badge variant={stats.conflicts > 0 ? 'destructive' : 'secondary'}>
            {stats.total} rules
          </Badge>
        </div>
      </div>
    );
  }

  return (
    <div className="rules-panel">
      <div className="panel-header">
        <h3>Tee Sheet Rules</h3>
        <div className="header-controls">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExtractRules}
            disabled={isExtracting}
            title="Extract rules from player requests"
          >
            <Sparkles className={cn("h-3 w-3 mr-1", isExtracting && "animate-pulse")} />
            {isExtracting ? 'Extracting...' : 'Extract Rules'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchRules}
            disabled={isLoading}
            title="Refresh rules"
          >
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="rules-stats">
        <div className="stat-item">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-item pending">
          <span className="stat-value">{stats.pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-item applied">
          <span className="stat-value">{stats.applied}</span>
          <span className="stat-label">Applied</span>
        </div>
        {stats.conflicts > 0 && (
          <div className="stat-item conflicts">
            <span className="stat-value">{stats.conflicts}</span>
            <span className="stat-label">Conflicts</span>
          </div>
        )}
      </div>

      {/* Conflicts Alert */}
      {conflicts.length > 0 && (
        <div className="conflicts-alert">
          <AlertTriangle className="h-4 w-4" />
          <span>{conflicts.length} rule conflict(s) detected</span>
          <Button
            variant="link"
            size="sm"
            onClick={() => onConflictSelect?.(conflicts)}
          >
            View <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      )}

      {error && (
        <div className="error-message">
          <AlertTriangle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {/* Rules by Type */}
      {stats.total === 0 && !isLoading ? (
        <div className="empty-state">
          <Sparkles className="h-8 w-8 text-muted-foreground" />
          <p>No rules extracted yet</p>
          <p className="text-sm text-muted-foreground">
            Click "Extract Rules" to analyze player requests
          </p>
        </div>
      ) : (
        <Accordion
          type="multiple"
          value={expandedTypes}
          onValueChange={setExpandedTypes}
          className="rules-accordion"
        >
          {renderRuleTypeSection('partner_group', grouped.partnerGroups)}
          {renderRuleTypeSection('time_constraint', grouped.timeConstraints)}
          {renderRuleTypeSection('time_preference', grouped.timePreferences)}
          {renderRuleTypeSection('manual_override', grouped.manualOverrides)}
          {grouped.systemDefaults?.length > 0 && (
            <AccordionItem value="system_defaults" className="rule-type-section type-gray">
              <AccordionTrigger className="rule-type-trigger">
                <div className="rule-type-header">
                  <Sparkles className="h-4 w-4" />
                  <span>System Defaults</span>
                  <Badge variant="secondary" className="rule-count">
                    {grouped.systemDefaults.length}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="rule-type-content">
                <div className="rules-list">
                  {grouped.systemDefaults.map(rule => renderRuleCard(rule))}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      )}
    </div>
  );
}

export default RulesPanel;
